import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";

import {
  doc,
  onSnapshot,
  collection,
  getDocs,
  query,
  where,
  limit,
  getDoc,
  updateDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useOrgRefdata, POSTGRES_REFDATA_ENABLED, probeOrgAccess } from "../data/useOrgRefdata";
import { createAccessGuard, BASIS_CONTROL, BASIS_MEMBER, UNSUPPORTED } from "../data/accessGuard";
import { ensureSupabaseRoleClaim } from "./supabaseClaim";
import UserContext from "../assets/user_context";
import { tracedCallable } from "../utilities/tracedCallable";
import { canRunPredictions } from "../utilities/prediction_utils";
import { isUsableFund } from "../utilities/general_util";
import { useAuth } from "./use-auth";
import { computeEffectiveOrgRoles } from "./orgRoles";
import { TransactionHookRegistryProvider } from "./hooks/transaction_registry";
import ThemeUserSync from "../theme/ThemeUserSync";

// Import race condition tester for development
if (import.meta.env.DEV) {
  import("../utilities/race_condition_tester.js");
}

//Logic flow is import here to manage whether all the user and org's data is loaded or not.
// A user that is loaded but not onboarded will have no orgs, and no org data
// A user that is loaded and onboarded, but in an org that isn't onboarded, will not need a subscription
// A user that is loaded and onboarded, and in an org that is onboarded, will need a subscription

//Loading order to determine loading state: user -> org -> accounts, funds, contacts, org data -> transactions

//Modals that will manage the loading process:
// 1. Select org modal
// 2. Subscribe modal
// 3. Create org modal
// 4. Org onboarding modal

// Function to check if Auth email differs from user state (real-time detection)
const checkAuthEmailSync = async (authUser, db) => {
  try {
    // Get current user document from Firestore to compare
    const userDocRef = doc(db, `users/${authUser.uid}`);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      const userData = userDoc.data();

      // If Auth email differs from stored Firestore email, sync it
      if (authUser.email !== userData.email) {
        await updateDoc(
          userDocRef,
          {
            email: authUser.email,
            emailSyncedAt: new Date().toISOString(),
          },
          { merge: true },
        );

        // Only show notification if sign-in sync hasn't already handled it
        if (!window.emailJustSyncedOnSignIn) {
          setTimeout(() => {
            alert(
              `Email successfully verified and updated to ${authUser.email}!`,
            );
          }, 500);
        }
      }
    }
  } catch (error) {
    console.error("Error in real-time email sync:", error);
  }
};

/**
 * How often returning to the tab may cost a membership probe. Long enough that
 * ordinary alt-tabbing is free, short enough that a revoked session does not
 * survive a coffee break.
 */
const ACCESS_CONFIRM_THROTTLE_MS = 60 * 1000;

const UserProvider = ({ children }) => {
  // Set default date range to show recent transactions (last 2 years to future)
  const defaultStartDate = dayjs().subtract(2, "year").startOf("month");
  const defaultEndDate = dayjs().add(1, "year").endOf("month");

  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState({});
  const [org, setOrgRaw] = useState(null); //This is the org the user is currently viewing
  const setOrg = (newOrgId) => {
    setOrgRaw(newOrgId);
    if (newOrgId) {
      try { localStorage.setItem("lastUsedOrgId", newOrgId); } catch (e) { /* ignore */ }
    }
  };
  const [orgData, setOrgData] = useState(null); //This is the org subscription status and more
  const [subscriptionData, setSubscriptionData] = useState(null); // New: dedicated subscription state
  const [accounts, setAccounts] = useState([]);
  const [accountsHierarchy, setAccountsHierarchy] = useState({});
  const [funds, setFunds] = useState([]);
  const [fundsHierarchy, setFundsHierarchy] = useState({});
  const [labels, setLabels] = useState([]);
  const [labelsHierarchy, setLabelsHierarchy] = useState({});
  const [contacts, setContacts] = useState([]);
  const [dateRangeForTx, setDateRangeForTx] = useState({
    firstDate: defaultStartDate,
    lastDate: defaultEndDate,
  });
  const [loaded, setLoaded] = useState(false);
  // false | true | { recommend: "basic"|"plus"|"advanced", featureLabel?: string }
  // — truthy opens the subscribe modal; the object form pre-recommends a plan
  const [openSubscribe, setOpenSubscribe] = useState(false);
  const [selectOrgOpen, setSelectOrgOpen] = useState(false);
  const [orgRoles, setOrgRoles] = useState([]);
  //Delte queue is an array of objects that have the id of the transaction to delete, so the user can see their request to delete imedaiately as a strikethrough
  const [deleteQueue, setDeleteQueue] = useState([]);
  const [contactMapping, setContactMapping] = useState({});
  const [contactAccountFundMatches, setContactAccountFundMatches] = useState(
    {},
  );
  const [mlModelState, setMlModelState] = useState(null);
  // True when at least one expectedTransactions doc with status: "open"
  // exists for this org. Drives the Tier-0 branch of canRunPredictions
  // so the imported-tx form locks while the matcher is run server-side
  // even on brand-new orgs with no contact history yet.
  const [hasOpenExpectedTransactions, setHasOpenExpectedTransactions] =
    useState(false);
  const [features, setFeatures] = useState({});
  const [donors, setDonors] = useState([]);
  const [donationSources, setDonationSources] = useState([]);
  //Track whether we've notified free trialers so we don't notify them again
  const [freeTrialNotified, setFreeTrialNotified] = useState(false);
  const [openTrialNotification, setOpenTrialNotification] = useState(false);
  const [controlSessions, setControlSessions] = useState([]);

  // Multi-subscription billing data

  // Broken org detection state
  const [brokenOrgDetected, setBrokenOrgDetected] = useState(false);
  const [brokenOrgId, setBrokenOrgId] = useState(null);

  const authHook = useAuth();

  // NEW: Proper loading state tracking
  const [loadingState, setLoadingState] = useState({
    userChecked: false, // Has auth state been checked?
    userLoaded: false, // Is user data loaded (or confirmed no user)?
    orgSelected: false, // Has an org been selected?
    orgDataLoaded: false, // Is org metadata loaded?
    accountsLoaded: false, // Are accounts loaded?
    fundsLoaded: false, // Are funds loaded?
    contactsLoaded: false, // Are contacts loaded?
    // donationSources starts [] before its listener resolves, which is
    // indistinguishable from "org has no sources" — DonationSetupGuard must
    // wait on this flag instead of redirecting on the empty initial value
    // (GH#307).
    donationSourcesLoaded: false,
  });

  // NEW: State for handling no-org users and invites
  const [inviteCheckState, setInviteCheckState] = useState({
    checking: false, // Currently checking for invites
    checked: false, // Have we checked for invites?
    found: false, // Were invites found?
    waiting: false, // Waiting for invite to be processed
  });

  // Track when modal was manually closed to prevent immediate reopening
  const lastManualCloseRef = useRef(0);
  // Track if this is the initial org load for the user
  const initialOrgLoadRef = useRef(true);
  // Track last org entry to prevent duplicate tracking
  const lastTrackedOrgRef = useRef(null);
  const lastTrackTimeRef = useRef(0);
  // Track unsubscribe functions for cleanup
  const unsubscribeFunctionsRef = useRef({});
  // Mirror controlSessions so the orgData onSnapshot closure can see the
  // current value (GH#117 guard would otherwise bail on take-control orgs
  // where the admin is not in data.users).
  const controlSessionsRef = useRef([]);
  // Mirror the Postgres path's reset for the same reason: the guard's callbacks
  // are created once (below) and would otherwise close over the first render's
  // hook result. resetOrgScopedState() clears the FIRESTORE slices only, so
  // without this the org a user was just removed from keeps being served out of
  // useOrgRefdata after the teardown ran.
  const pgResetRef = useRef(null);
  // Last time the access guard was asked to confirm, so returning to a tab
  // repeatedly does not issue an RPC per alt-tab.
  const lastAccessConfirmRef = useRef(0);
  // The throttled probe itself. BOTH triggers go through it — returning to the
  // tab, and a realtime channel stuck at its backoff ceiling (C17) — so a stuck
  // channel and an alt-tab cannot spend two RPCs on one question. Stable
  // identity: it reads only refs and a module constant, so the data stack below
  // is never rebuilt because this was re-created.
  const confirmAccessThrottled = useCallback(() => {
    if (!POSTGRES_REFDATA_ENABLED) return;
    const now = Date.now();
    if (now - lastAccessConfirmRef.current < ACCESS_CONFIRM_THROTTLE_MS) return;
    lastAccessConfirmRef.current = now;
    // confirm() classifies its own failures and never rejects.
    void accessGuardRef.current?.confirm();
  }, []);

  // N2: the revoked-access guard (GH#117, charter §16.4 decision 2).
  //
  // Under Firestore, losing access to an org ARRIVES: the org document listener
  // re-fires with the user absent from data.users, and the branch at the top of
  // setupOrgListeners tears the session down. Under RLS nothing arrives — the
  // row simply stops being readable and the next call is refused. This guard is
  // that missing event, reconstructed from the refusal, and it routes to the
  // SAME teardown so both paths end a revoked session identically.
  //
  // Created once and held in a ref: its callbacks close over nothing but refs
  // and state setters, all of which are referentially stable, and re-creating
  // it per render would reset the once-per-org latch that keeps a fan-out of
  // rejected calls from tearing down repeatedly.
  const accessGuardRef = useRef(null);
  if (!accessGuardRef.current) {
    accessGuardRef.current = createAccessGuard({
      // The authoritative membership probe. Without it confirm() answers
      // UNSUPPORTED and the escalation path below is inert — which is why it is
      // wired here rather than left as documented-but-unused parameter surface.
      probeAccess: probeOrgAccess,

      // The same fact the Firestore branch consults, from the same ref: an
      // admin holding an org by take-control is not a member of it, and when
      // their session ends the server raises the byte-identical refusal a
      // removed member gets. Only the client knows which one this is.
      getAccessBasis: (id) =>
        controlSessionsRef.current.some((cs) => cs.orgId === id)
          ? BASIS_CONTROL
          : BASIS_MEMBER,

      // TAGS, not `extra`. Sentry indexes tags and does not index extra, so
      // "which orgs saw a revocation this week, and are they all one org?" —
      // the single question this event exists to answer — is unanswerable while
      // the org id sits in an unindexed blob. `reason` is free-form prose and is
      // the one field that genuinely belongs in extra.
      onAccessRevoked: ({ orgId: lostOrg, reason, code }) => {
        Sentry.withScope((scope) => {
          scope.setTag("component", "accessGuard");
          scope.setTag("action", "org_access_revoked");
          scope.setTag("org.id", lostOrg ?? "none");
          scope.setTag("code", code ?? "none");
          scope.setExtra("reason", reason);
          Sentry.captureMessage("org access revoked (postgres)", "warning");
        });
        cleanupOrgListeners();
        setOrgRoles([]);
        resetOrgScopedState();
        // Both sources, or the teardown is a half-teardown: resetOrgScopedState
        // empties the Firestore state, this drops the Postgres snapshot and its
        // channels.
        pgResetRef.current?.();
      },

      // A support visit ending normally is not a security event. Same teardown,
      // no alarm — the admin's own account lost nothing.
      onControlSessionEnded: ({ orgId: lostOrg, reason, code }) => {
        Sentry.addBreadcrumb({
          category: "access",
          level: "info",
          message: "admin control session ended",
          data: { org_id: lostOrg, reason, code },
        });
        cleanupOrgListeners();
        setOrgRoles([]);
        resetOrgScopedState();
        pgResetRef.current?.();
      },

      // Everything the guard decided NOT to act on. Purely observational, and
      // the reason a misclassification shows up in logs instead of silently.
      //
      // TRANSIENT stays a breadcrumb: a dropped socket or a token mid-refresh is
      // weather, and one event per occurrence would bury everything else.
      //
      // UNSUPPORTED does not. PGRST202/203/204 means the database does not have
      // the function the client just called — deploy skew, the exact failure a
      // phased schema rollout produces and the one the classifier was written to
      // separate out. Collapsed into the same breadcrumb it produced NO Sentry
      // signal whatever: a breadcrumb is transmitted only when some later
      // capture carries it, and until this chunk nothing in src/data/ captured
      // anything at all. "Is the new RPC actually live in this environment, or
      // is every client getting PGRST202?" is a deploy-day question and it needs
      // an event.
      onTransient: ({ orgId: forOrg, verdict, reason, code }) => {
        if (verdict === UNSUPPORTED) {
          Sentry.withScope((scope) => {
            scope.setTag("component", "accessGuard");
            scope.setTag("action", "rpc_unsupported");
            scope.setTag("org.id", forOrg ?? "none");
            scope.setTag("code", code ?? "none");
            scope.setExtra("reason", reason);
            Sentry.captureMessage("RPC missing from the schema cache (deploy skew)", "error");
          });
          return;
        }
        // `info`, not `debug` — audit-logging check 9 permits two levels and
        // debug is not one of them.
        Sentry.addBreadcrumb({
          category: "access",
          level: "info",
          message: `access check: ${verdict}`,
          data: { org_id: forOrg, reason, code },
        });
      },
    });
  }

  // N2: the Postgres path, running ALONGSIDE the listeners below. Off unless
  // VITE_USE_POSTGRES_REFDATA is set, so this changes nothing by default. While
  // it is on, the eight org-scoped listeners still run — both sources stay live
  // so they can be compared on real data before any of them is deleted.
  const pgRefdata = useOrgRefdata(org, {
    enabled: POSTGRES_REFDATA_ENABLED,
    uid: user?.uid ?? null,
    onAccessError: (error, forOrg) =>
      accessGuardRef.current.report(error, { forOrg }),
    // C17, the other half of the seam described below: a realtime channel that
    // has given up rejoining is what a revoked membership looks like from the
    // transport, and it is the one trigger that does not need the user to come
    // back to the tab. Throttled through the SAME ref as the visibility
    // trigger, so a stuck channel and an alt-tab cannot issue two probes for
    // one question.
    onTransportDegraded: confirmAccessThrottled,
  });
  // Mirrored in an effect rather than during render: `reset` is referentially
  // stable, and a ref written during render is not safe under concurrent
  // rendering. The guard's callbacks only ever fire from a rejected RPC, which
  // is always after the first commit.
  useEffect(() => {
    pgResetRef.current = pgRefdata.reset;
  }, [pgRefdata.reset]);

  // WHICH STORE FED THIS SESSION — the single most important dimension in the
  // whole migration, and until now it was recorded nowhere.
  //
  // `refSource` (~1250 lines below) silently picks between the Postgres snapshot
  // and the Firestore listeners on one test, `pgRefdata.loaded`, and both
  // outcomes look identically healthy on screen. Without this tag every Sentry
  // event raised anywhere in the app during the dual-run is ambiguous about
  // where its accounts, funds, labels and three hierarchies came from — which
  // makes "my chart of accounts is missing a group" unactionable, and makes the
  // whole Firestore-vs-Postgres comparison unfalsifiable from production data.
  //
  // TWO tags rather than one, because the pair answers the question the single
  // value cannot: `data.pg_enabled=true` together with `data.source=firestore`
  // is exactly the SILENT FALLBACK — the flag is on, org_bootstrap is failing,
  // and the app is quietly serving listener data instead. That is the state
  // this wave most needs to be able to see and cannot infer from either tag
  // alone.
  useEffect(() => {
    Sentry.setTag("data.source", pgRefdata.loaded ? "postgres" : "firestore");
    Sentry.setTag("data.pg_enabled", String(POSTGRES_REFDATA_ENABLED));
  }, [pgRefdata.loaded]);

  // Point the guard at the open org. Re-arming on a switch is what keeps a
  // refusal for the org the user just LEFT from tearing down the one they just
  // arrived at.
  useEffect(() => {
    const guard = accessGuardRef.current;
    if (org) guard.open(org);
    else guard.close();
  }, [org]);

  // ASK, rather than waiting to be refused.
  //
  // Everything else that can reach the guard is reactive: org_bootstrap on open
  // and on invalidate, events_since from resync. A member removed mid-session
  // issues neither. Their private channel stops authorizing, so it never reaches
  // SUBSCRIBED, so resync never resumes, so no RPC is ever refused — and the
  // session stays fully live until the tab is reloaded. That is the GH#117
  // regression this guard exists to close, masked today only because the
  // Firestore listeners still run alongside.
  //
  // Returning to the tab is the trigger that does not depend on the transport
  // being replaced: it is when the user is about to act on what they see, and it
  // costs one RPC. Throttled because visibilitychange fires on every alt-tab.
  //
  // The second trigger (C17, now wired): a channel that keeps failing to rejoin.
  // src/data/realtime.js calls `onDegraded` once when its backoff hits the
  // ceiling, useOrgRefdata carries it across, and it lands on the SAME throttled
  // probe as the visibility trigger above — because a tab left in the foreground
  // all afternoon issues nothing of its own, and a revoked member's channel is
  // precisely one that can no longer authorize.
  useEffect(() => {
    if (!POSTGRES_REFDATA_ENABLED || !org || typeof document === "undefined") {
      return undefined;
    }
    // A newly opened org has never been confirmed, whatever the previous one cost.
    lastAccessConfirmRef.current = 0;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      confirmAccessThrottled();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [org, confirmAccessThrottled]);

  // Helper function to update loading state
  const updateLoadingState = (updates) => {
    setLoadingState((prev) => ({ ...prev, ...updates }));
  };

  // Note: Loading state is now set directly in the listeners (lines 610, 647, 680)
  // when data arrives (even if empty). Loading state is reset to false when org
  // changes (line 1110-1116). No need for separate useEffects to track array length.

  useEffect(() => {
    if (org && orgData && !loadingState.orgDataLoaded) {
      updateLoadingState({ orgDataLoaded: true });
    } else if (org && !orgData && loadingState.orgDataLoaded) {
      updateLoadingState({ orgDataLoaded: false });
    }
  }, [orgData, org, loadingState.orgDataLoaded]);

  // Effective org roles, derived (not set inside the orgData snapshot). A
  // T3Admin/superAdmin who "takes control" of an org usually isn't in the
  // org's users map, so they have no member roles; while they hold an active
  // control session for this org we append an effective "admin" role so the
  // org-admin-gated UI (enter transactions, reconcile, contact/donor/fund/label
  // edit) is usable while supporting the org. Deriving from orgData +
  // controlSessions means the role is correct regardless of which Firestore
  // listener resolves first, and it clears the moment control is released or
  // the org changes (orgData -> null). See GH#181.
  //
  // Reads the Firestore `orgData` DIRECTLY, and must keep doing so until it can
  // depend on whatever replaces it. `refSource` is declared ~1100 lines below
  // and resolves orgData to this same value today, so naming it here would be a
  // forward reference that changes nothing — except on the day the billing wave
  // moves orgData onto the Postgres path, when this effect would run once with
  // null and never again, because nothing in its dependency array changes when
  // the Postgres org document arrives. computeEffectiveOrgRoles would return []
  // and canManageOrg() would refuse everything: the app goes read-only, silently.
  // Whoever moves orgData moves this dependency with it.
  useEffect(() => {
    setOrgRoles(
      computeEffectiveOrgRoles({
        orgData,
        controlSessions,
        org,
        uid: user?.uid,
      }),
    );
  }, [orgData, controlSessions, org, user]);

  // Compute if everything is loaded - now using actual state
  const isFullyLoaded = () => {
    const state = loadingState;

    // If no user, just need to have checked
    if (!user) {
      return state.userChecked;
    }

    // If user but no org selected yet, check invite state
    if (!org) {
      // If we're still checking for invites or waiting for them to be processed, not loaded
      if (inviteCheckState.checking || inviteCheckState.waiting) {
        return false;
      }

      // If we've checked and found no invites, then we're ready for user to create org
      if (inviteCheckState.checked && !inviteCheckState.found) {
        return state.userChecked && state.userLoaded;
      }

      // Default case - still checking
      return false;
    }

    // Basic requirements: user checked, user loaded, org selected, org data loaded
    const basicRequirements =
      state.userChecked &&
      state.userLoaded &&
      state.orgSelected &&
      state.orgDataLoaded &&
      !!orgData;

    if (!basicRequirements) {
      return false;
    }

    // If org is not onboarded, we only need org data - no accounts/funds/contacts required
    if (orgData && orgData.onboarded === false) {
      return true;
    }

    // If org is onboarded, then we need all the data
    const onboardedOrgRequirements =
      state.accountsLoaded &&
      state.fundsLoaded &&
      state.contactsLoaded &&
      // Double-check actual state has data
      accounts.length > 0 &&
      funds.length > 0 &&
      contacts.length > 0;

    return basicRequirements && onboardedOrgRequirements;
  };

  // Update loaded state based on loading state
  useEffect(() => {
    const shouldBeLoaded = isFullyLoaded();
    if (shouldBeLoaded !== loaded) {
      setLoaded(shouldBeLoaded);
    }
  }, [
    loadingState,
    user,
    org,
    loaded,
    accounts.length,
    funds.length,
    contacts.length,
    orgData,
  ]);

  // Helper function to track org entry
  const trackOrgEntry = async (orgId) => {
    // Prevent duplicate tracking within 5 seconds
    const now = Date.now();
    if (
      lastTrackedOrgRef.current === orgId &&
      now - lastTrackTimeRef.current < 5000
    ) {
      return;
    }

    if (authHook.functions && orgId && user && orgId !== org) {
      try {
        lastTrackedOrgRef.current = orgId;
        lastTrackTimeRef.current = now;

        const trackAuthEvent = tracedCallable(
          authHook.functions,
          "trackAuthEvent",
        );
        await trackAuthEvent({
          action: "entered_org",
          orgId: orgId,
          metadata: {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            timestamp: new Date().toISOString(),
            previousOrg: org || null,
          },
        });
      } catch (trackError) {
        console.error("Failed to track org entry:", trackError);
        // Don't block org loading if tracking fails
      }
    }
  };

  // Helper function to mark that modal was manually closed (for specific org)
  const markManualModalClose = () => {
    lastManualCloseRef.current = {
      timestamp: Date.now(),
      orgId: org,
    };
  };

  // Resolve the best fallback org: defaultOrgId → lastUsedOrgId → null
  // Returns null if neither default nor last-used is available, so the
  // org selector modal can be shown to first-time multi-org users.
  const resolveFallbackOrg = (data) => {
    const orgs = data?.orgs || [];
    if (orgs.length === 0) return null;

    const isValid = (id) => id && orgs.some((o) => o.orgID === id);

    if (isValid(data.defaultOrgId)) return data.defaultOrgId;

    try {
      const lastUsed = localStorage.getItem("lastUsedOrgId");
      if (isValid(lastUsed)) return lastUsed;
    } catch (e) { /* ignore */ }

    return null;
  };

  // Helper function to check if modal was recently closed manually (for THIS org)
  const wasRecentlyClosedManually = () => {
    if (!lastManualCloseRef.current) return false;

    const { timestamp, orgId } = lastManualCloseRef.current;

    // Only apply protection if it was THIS org that was closed
    if (orgId !== org) return false;

    return Date.now() - timestamp < 3000; // 3 second protection
  };

  // Helper function to check for invites for a user email
  const checkForInvites = async (userEmail) => {
    setInviteCheckState((prev) => ({ ...prev, checking: true }));

    // Check if we're in local development mode
    const isLocalDev =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    try {
      if (isLocalDev) {
        // In local dev, check Firestore directly
        const invitesQuery = query(
          collection(authHook.db, "invites"),
          where("email", "==", userEmail),
        );

        const invitesSnapshot = await getDocs(invitesQuery);
        const hasInvites = !invitesSnapshot.empty;

        setInviteCheckState({
          checking: false,
          checked: true,
          found: hasInvites,
          waiting: hasInvites,
        });

        return hasInvites;
      } else {
        // Use cloud function for production/deployed environments
        const checkInvitesForUser = tracedCallable(
          authHook.functions,
          "checkInvitesForUser",
        );
        const result = await checkInvitesForUser({ email: userEmail });

        const hasInvites = result.data.hasInvites;
        setInviteCheckState({
          checking: false,
          checked: true,
          found: hasInvites,
          waiting: hasInvites,
        });

        return hasInvites;
      }
    } catch (error) {
      console.error("Error checking for invites:", error);
      setInviteCheckState({
        checking: false,
        checked: true,
        found: false,
        waiting: false,
      });
      return false;
    }
  };

  // Helper to clean up org listeners
  const cleanupOrgListeners = () => {
    Object.values(unsubscribeFunctionsRef.current).forEach((unsub) => {
      if (typeof unsub === "function") unsub();
    });
    unsubscribeFunctionsRef.current = {};
  };

  // Reset every org-scoped slice back to its empty/null baseline. Shared by the
  // org-switch teardown and the access-revoked branch so BOTH clear the exact
  // same full set — otherwise a subset clear leaves the previous org's data
  // frozen on screen after its listeners are gone.
  const resetOrgScopedState = () => {
    setAccounts([]);
    setFunds([]);
    setAccountsHierarchy({});
    setFundsHierarchy({});
    setLabels([]);
    setLabelsHierarchy({});
    setContacts([]);
    setOrgData(null);
    setSubscriptionData(null);
    setContactMapping({});
    setContactAccountFundMatches({});
    setDonationSources([]);
    // setLoadingState directly (not updateLoadingState) so this function stays
    // referentially stable for the effects that call it during teardown.
    setLoadingState((prev) => ({ ...prev, donationSourcesLoaded: false }));
  };

  // Extract listener setup into a separate function for reuse
  const setupOrgListeners = () => {
    if (!authHook?.db || !user || !org) return;
    // Idempotent: if the org listeners are already active, bail. Both callers
    // (the org-load effect and the 5s recovery) run cleanupOrgListeners first,
    // so this is a no-op for them; it only guards the take-control re-setup
    // path (#181) from double-subscribing regardless of effect ordering.
    if (unsubscribeFunctionsRef.current.orgData) return;

    // Every branch that will never receive a donationSources snapshot has to
    // settle the flag itself, or DonationSetupGuard spins forever.
    const settleDonationSourcesEmpty = () => {
      setDonationSources([]);
      updateLoadingState({ donationSourcesLoaded: true });
    };

    //Get the org data
    unsubscribeFunctionsRef.current.orgData = onSnapshot(
      doc(authHook.db, "orgs", org),
      (orgDoc) => {
        const data = orgDoc.data();

        if (data) {
          // A stale listener can fire after the user has been removed from
          // this org's users map (revoked access, role change, or a wrong
          // lastUsedOrgId). data.users[uid] is then undefined — accessing
          // .roles crashes the app. Bail and let cleanupOrgListeners run.
          // See GH#117.
          //
          // Exception: T3Admin/superAdmin take-control. The admin is not in
          // data.users on customer orgs but should still load orgData; we
          // detect that via an active adminControlSession for this org.
          const memberRecord = data.users?.[user.uid];
          const hasControlSession = controlSessionsRef.current.some(
            (s) => s.orgId === org,
          );
          if (!memberRecord && !hasControlSession) {
            // Access was revoked — mirror the org-switch teardown so the
            // previous org's data doesn't keep rendering (frozen at the last
            // snapshot) after its listeners are torn down. Clear the FULL
            // org-scoped set, not just a subset.
            cleanupOrgListeners();
            setOrgRoles([]);
            resetOrgScopedState();
            return;
          }
          setOrgData(data);
          // orgRoles (including the take-control effective-admin grant) is
          // derived from orgData + controlSessions in a dedicated effect below,
          // so it stays correct regardless of which listener resolves first and
          // clears when control is released. See the orgRoles effect (#181).
          Sentry.setTag("orgId", org);

          if (data.features) {
            setFeatures(data.features);
            //if features.donors is true, fetch the donors
            if (data.features.donorManagement) {
              // Load donors from collection (orgs/{orgId}/donors)
              // Filter out merged donors (those with mergedInto field)
              // This orgData snapshot re-fires on every org doc change, so
              // unsubscribe the previous listener before reassigning —
              // otherwise a leaked org-A listener keeps injecting org-A data
              // while org B is active.
              if (unsubscribeFunctionsRef.current.donors) {
                unsubscribeFunctionsRef.current.donors();
              }
              unsubscribeFunctionsRef.current.donors = onSnapshot(
                collection(authHook.db, "orgs", org, "donors"),
                (snapshot) => {
                  const donorsData = snapshot.docs
                    .map((doc) => {
                      const data = doc.data();
                      return {
                        ...data,
                        id: Array.isArray(data.id) ? data.id : [doc.id],
                        firestoreId: doc.id,
                      };
                    })
                    .filter((donor) => !donor.mergedInto); // Exclude merged donors
                  setDonors(donorsData);
                },
                (error) => {
                  // Permission errors are expected when access is revoked
                  // mid-session — silence them
                  if (error?.code !== "permission-denied") {
                    Sentry.withScope((scope) => {
                      scope.setTag("component", "context_and_index");
                      scope.setTag("action", "donors_listener");
                      scope.setTag("org_id", org);
                      Sentry.captureException(error);
                    });
                  }
                  setDonors([]);
                },
              );
              // Load donation sources
              if (unsubscribeFunctionsRef.current.donationSources) {
                unsubscribeFunctionsRef.current.donationSources();
              }
              unsubscribeFunctionsRef.current.donationSources = onSnapshot(
                doc(authHook.db, "orgs", org, "donorManagement", "donationSources"),
                (sourcesDoc) => {
                  if (sourcesDoc.exists()) {
                    const data = sourcesDoc.data();
                    const sourcesData = data.sources || [];
                    setDonationSources(sourcesData);
                    updateLoadingState({ donationSourcesLoaded: true });
                  } else {
                    settleDonationSourcesEmpty();
                  }
                },
                (error) => {
                  // Permission errors are expected when access is revoked
                  // mid-session — silence them
                  if (error?.code !== "permission-denied") {
                    Sentry.withScope((scope) => {
                      scope.setTag("component", "context_and_index");
                      scope.setTag("action", "donation_sources_listener");
                      scope.setTag("org_id", org);
                      Sentry.captureException(error);
                    });
                  }
                  // Fail OPEN: mark loaded even on error so DonationSetupGuard
                  // redirects to setup instead of spinning forever.
                  settleDonationSourcesEmpty();
                },
              );
            } else {
              setDonors([]);
              settleDonationSourcesEmpty();
            }

            // Labels load regardless of the entitlement (§6.3): committed
            // label names must keep rendering read-only after a downgrade.
            // Create/edit/assign surfaces gate on features.labels themselves.
            if (unsubscribeFunctionsRef.current.labels) {
              unsubscribeFunctionsRef.current.labels();
            }
            unsubscribeFunctionsRef.current.labels = onSnapshot(
              collection(authHook.db, "orgs", org, "labels"),
              (snapshot) => {
                const newLabels = snapshot.docs.map((doc) => ({
                  ...doc.data(),
                  id: doc.id,
                }));
                setLabels(newLabels);
              },
            );

            if (unsubscribeFunctionsRef.current.labelsHierarchy) {
              unsubscribeFunctionsRef.current.labelsHierarchy();
            }
            unsubscribeFunctionsRef.current.labelsHierarchy = onSnapshot(
              doc(
                authHook.db,
                "orgs",
                org,
                "organizationAndSettings",
                "labelsHierarchy",
              ),
              (doc) => {
                if (doc.data() !== undefined) {
                  setLabelsHierarchy(doc.data());
                } else {
                  setLabelsHierarchy({});
                }
              },
            );
          } else {
            setFeatures({});
            setDonors([]);
            settleDonationSourcesEmpty();
            setLabels([]);
            setLabelsHierarchy({});
          }
        } else {
          // Org doc missing — no donation-sources listener will ever run, so
          // settle the flag here rather than leaving consumers spinning.
          settleDonationSourcesEmpty();
        }
      },
    );

    // Get subscription data from new location
    unsubscribeFunctionsRef.current.subscription = onSnapshot(
      doc(authHook.db, "orgs", org, "organizationAndSettings", "subscription"),
      (subDoc) => {
        const subData = subDoc.data();
        setSubscriptionData(subData);
        if (subData?.status) {
          Sentry.setTag("subscriptionStatus", subData.status);
        }

        if (subData) {
          // Also update orgData for backward compatibility
          setOrgData((prevOrgData) => ({
            ...prevOrgData,
            subscriptionData: subData,
            subscriptionCurrent: subData.subscriptionCurrent,
          }));
        }
      },
    );

    //Fetch Accounts
    unsubscribeFunctionsRef.current.accounts = onSnapshot(
      collection(authHook.db, "orgs", org, "accounts"),
      (snapshot) => {
        const newAccounts = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));
        setAccounts(newAccounts);
        updateLoadingState({ accountsLoaded: true });
      },
    );

    //Fetch Account Hierarchy
    unsubscribeFunctionsRef.current.accountsHierarchy = onSnapshot(
      doc(
        authHook.db,
        "orgs",
        org,
        "organizationAndSettings",
        "accountsHierarchy",
      ),
      (doc) => {
        if (doc.data() !== undefined) {
          setAccountsHierarchy(doc.data());
        } else {
          setAccountsHierarchy({});
        }
      },
    );

    //Fetch the funds
    unsubscribeFunctionsRef.current.funds = onSnapshot(
      collection(authHook.db, "orgs", org, "funds"),
      (snapshot) => {
        const newFunds = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
        }));
        setFunds(newFunds);
        updateLoadingState({ fundsLoaded: true });
      },
    );

    //Fetch Fund Hierarchy
    unsubscribeFunctionsRef.current.fundsHierarchy = onSnapshot(
      doc(
        authHook.db,
        "orgs",
        org,
        "organizationAndSettings",
        "fundsHierarchy",
      ),
      (doc) => {
        if (doc.data() !== undefined) {
          setFundsHierarchy(doc.data());
        } else {
          setFundsHierarchy({});
        }
      },
    );

    //Fetch the contacts
    // Filter out merged contacts (those with mergedInto field)
    unsubscribeFunctionsRef.current.contacts = onSnapshot(
      collection(authHook.db, "orgs", org, "contacts"),
      (snapshot) => {
        const newContacts = snapshot.docs
          .map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }))
          .filter((c) => !c.mergedInto);
        setContacts(newContacts);
        updateLoadingState({ contactsLoaded: true });
      },
    );

    //Fetch contact account fund matches for predictions
    unsubscribeFunctionsRef.current.contactAccountFundMatches = onSnapshot(
      doc(
        authHook.db,
        "orgs",
        org,
        "organizationAndSettings",
        "contactAccountFundMatches",
      ),
      (doc) => {
        if (doc.exists()) {
          setContactAccountFundMatches(doc.data());
        } else {
          setContactAccountFundMatches({});
        }
      },
    );

    // Fetch ML model state for prediction readiness awareness
    unsubscribeFunctionsRef.current.mlModelState = onSnapshot(
      doc(
        authHook.db,
        "orgs",
        org,
        "organizationAndSettings",
        "mlModelState",
      ),
      (doc) => {
        if (doc.exists()) {
          setMlModelState(doc.data());
        } else {
          setMlModelState(null);
        }
      },
    );

    // Watch for any open expectedTransactions doc — drives the Tier-0
    // branch of canRunPredictions. limit(1) is sufficient because we
    // only care about the boolean "any open?". Index is automatic
    // (single-field equality on status).
    unsubscribeFunctionsRef.current.expectedTransactions = onSnapshot(
      query(
        collection(authHook.db, "orgs", org, "expectedTransactions"),
        where("status", "==", "open"),
        limit(1),
      ),
      (snapshot) => {
        setHasOpenExpectedTransactions(!snapshot.empty);
      },
    );
  };

  // Load the user's data
  useEffect(() => {
    if (authHook.auth && !authHook.isLoadingFirebase) {
      const unsubscribe = onAuthStateChanged(authHook.auth, async (user) => {

        if (user) {
          // N1: sessions that were open before the claim backfill still hold a
          // token without `role: "authenticated"`. Conditional and
          // self-disarming — a token that already carries the claim costs one
          // cached read and no network call. Not awaited: nothing in this app
          // reads the claim, so it must not sit on the load path.
          ensureSupabaseRoleClaim(user).catch((error) =>
            Sentry.captureException(error, { tags: { area: "supabase_claim" } }),
          );

          // Check for email verification completion (real-time detection)
          await checkAuthEmailSync(user, authHook.db);

          setUser(user);
          Sentry.setUser({ id: user.uid, email: user.email });
          updateLoadingState({ userChecked: true });
        } else {
          // User logged out - release any active control sessions (T3Admins only)
          // Check if user is a T3Admin by checking the admins/permissions document
          if (authHook.functions && authHook.db && user) {
            try {
              // Only check for control sessions if user might be a T3Admin
              // This avoids unnecessary calls for 99% of users
              const permissionsRef = doc(authHook.db, "admins", "permissions");
              const permissionsSnap = await getDoc(permissionsRef);

              if (permissionsSnap.exists()) {
                const permissionsData = permissionsSnap.data();
                const isT3Admin =
                  permissionsData.T3Admins &&
                  permissionsData.T3Admins.includes(user.uid);
                const isSuperAdmin =
                  permissionsData.superAdmins &&
                  permissionsData.superAdmins.includes(user.uid);

                // Only fetch and release control sessions if user is a system admin
                if (isT3Admin || isSuperAdmin) {
                  const getMyControlSessions = tracedCallable(
                    authHook.functions,
                    "getMyControlSessions",
                  );
                  const result = await getMyControlSessions();

                  if (result.data.success && result.data.sessions.length > 0) {
                    // Release all active control sessions
                    const releaseControlOfOrg = tracedCallable(
                      authHook.functions,
                      "releaseControlOfOrg",
                    );

                    await Promise.all(
                      result.data.sessions.map(async (session) => {
                        try {
                          await releaseControlOfOrg({ orgId: session.orgId });
                        } catch (err) {
                          console.error(
                            `Failed to release control of org ${session.orgName}:`,
                            err,
                          );
                        }
                      }),
                    );
                  }
                }
              }
            } catch (error) {
              // Don't block logout if check/release fails
            }
          }

          setUser(null);
          setUserData({});
          Sentry.setUser(null);
          setOrg(null);
          try { localStorage.removeItem("lastUsedOrgId"); } catch (e) { /* ignore */ }
          // NEW: Clear all org-related state on logout to prevent inheritance by new users
          resetOrgScopedState();
          setOrgRoles([]);
          setFeatures({});
          setDonors([]);
          setDeleteQueue([]);
          setControlSessions([]);
          cleanupOrgListeners();
          updateLoadingState({
            userChecked: true,
            userLoaded: true,
            orgSelected: false,
            orgDataLoaded: false,
            accountsLoaded: false,
            fundsLoaded: false,
            contactsLoaded: false,
          });
        }
      });
      return () => {
        unsubscribe();
      };
    }
  }, [authHook, authHook?.auth, authHook?.isLoadingFirebase]);

  // Load the user's orgs, and check for more than one org
  useEffect(() => {
    if (authHook && !authHook.isLoadingFirebase && user) {
      const unsub = onSnapshot(
        doc(authHook.db, "users", user.uid),
        async (doc) => {
          const data = doc.data();

          // Safety check: ensure userData is never completely undefined
          setUserData(data || {});

          // Only update userLoaded if it's not already true to prevent repeated updates
          if (!loadingState.userLoaded) {
            updateLoadingState({ userLoaded: true });
          }

          if (data && data.orgs && data.orgs.length > 0) {
            // User has orgs - stop waiting for invites if we were
            if (inviteCheckState.waiting) {
              setInviteCheckState({
                checking: false,
                checked: true,
                found: true,
                waiting: false,
              });
            }

            //Check if the user is in more than one org
            if (data.orgs.length > 1) {
              if (!wasRecentlyClosedManually()) {
                // Check if user just logged in after logging out
                // If so, use default org instead of URL org
                const justLoggedOut = sessionStorage.getItem("justLoggedOut");
                if (justLoggedOut) {
                  sessionStorage.removeItem("justLoggedOut");

                  // Clear URL org parameter if present
                  const urlParams = new URLSearchParams(window.location.search);
                  if (urlParams.has("org")) {
                    urlParams.delete("org");
                    const newUrl = urlParams.toString()
                      ? `${window.location.pathname}?${urlParams.toString()}`
                      : window.location.pathname;
                    window.history.replaceState({}, "", newUrl);
                  }

                  // Use default or last-used org if available
                  setTimeout(() => {
                    if (!org) {
                      const resolvedOrg = resolveFallbackOrg(data);
                      if (resolvedOrg) {
                        setOrg(resolvedOrg);
                      } else {
                        setOrg(data.orgs[0].orgID);
                        setSelectOrgOpen(true);
                      }
                    }
                  }, 250);
                  return; // Skip normal URL handling
                }

                // Check if URL has an org parameter - if so, let URL manager handle it
                const urlParams = new URLSearchParams(window.location.search);
                const urlOrgId = urlParams.get("org");

                if (!urlOrgId) {
                  // No URL org parameter, proceed with fallback logic
                  setTimeout(() => {
                    if (!org) {
                      const resolvedOrg = resolveFallbackOrg(data);
                      if (resolvedOrg) {
                        setOrg(resolvedOrg);
                      } else {
                        setOrg(data.orgs[0].orgID);
                        setSelectOrgOpen(true);
                      }
                    }
                  }, 250);
                }
              }
            } else if (data.orgs.length === 1) {
              //Set the user's org to the only org they are in
              const newOrgId = data.orgs[0].orgID;
              if (newOrgId !== org) {
                setOrg(newOrgId);
                // Track org entry for single org users on initial load
                if (initialOrgLoadRef.current) {
                  setTimeout(() => {
                    trackOrgEntry(newOrgId);
                    initialOrgLoadRef.current = false;
                  }, 1000);
                }
              }
            }
          } else {
            // User has no orgs - check for invites if we haven't already
            if (!inviteCheckState.checked && !inviteCheckState.checking) {
              const userEmail = data?.email || user?.email;

              if (!userEmail) {
                setInviteCheckState({
                  checking: false,
                  checked: true,
                  found: false,
                  waiting: false,
                });
                updateLoadingState({
                  orgSelected: true,
                  orgDataLoaded: true,
                  accountsLoaded: true,
                  fundsLoaded: true,
                  contactsLoaded: true,
                });
                return;
              }

              const hasInvites = await checkForInvites(userEmail);

              if (!hasInvites) {
                updateLoadingState({
                  orgSelected: true,
                  orgDataLoaded: true,
                  accountsLoaded: true,
                  fundsLoaded: true,
                  contactsLoaded: true,
                });
              }
            } else if (inviteCheckState.checked && !inviteCheckState.found) {
              updateLoadingState({
                orgSelected: true,
                orgDataLoaded: true,
                accountsLoaded: true,
                fundsLoaded: true,
                contactsLoaded: true,
              });
            }
          }
        },
      );
      return () => unsub();
    }
  }, [
    authHook,
    authHook.db,
    user,
    inviteCheckState.checked,
    inviteCheckState.checking,
  ]);

  // Load control sessions for T3Admins/superAdmins
  useEffect(() => {
    if (!authHook?.db || !authHook?.functions || !user) {
      setControlSessions([]);
      return;
    }

    // Real-time listener for active control sessions
    const sessionsQuery = query(
      collection(authHook.db, "adminControlSessions"),
      where("t3AdminUid", "==", user.uid),
      where("endTime", "==", null),
    );

    const unsubscribe = onSnapshot(
      sessionsQuery,
      (snapshot) => {
        const sessions = snapshot.docs.map((doc) => ({
          sessionId: doc.id,
          ...doc.data(),
        }));
        controlSessionsRef.current = sessions;
        setControlSessions(sessions);
      },
      (error) => {
        // Permission errors are expected for non-admin users — silence them
        if (error?.code !== "permission-denied") {
          console.error("Error fetching control sessions:", error);
        }
        setControlSessions([]);
      },
    );

    return () => unsubscribe();
  }, [authHook?.db, authHook?.functions, user?.uid]);

  // Lock org to controlled org when admin has active control sessions (fixes #88)
  // On page refresh, controlSessions loads after the initial org resolution,
  // so we need to correct the org once sessions are known.
  useEffect(() => {
    if (controlSessions.length > 0) {
      const controlledOrgId = controlSessions[0].orgId;
      if (org !== controlledOrgId) {
        setOrg(controlledOrgId);
      }
      // Close org selector if it was opened during the race
      if (selectOrgOpen) {
        setSelectOrgOpen(false);
      }
    }
  }, [controlSessions]);

  // Load billing account data for multi-subscription management
  // React to URL changes for multi-org users - fixes intermittent loading hang
  useEffect(() => {
    if (
      user &&
      userData?.orgs &&
      userData.orgs.length > 1 &&
      !org &&
      !wasRecentlyClosedManually()
    ) {
      const urlParams = new URLSearchParams(window.location.search);
      const urlOrgId = urlParams.get("org");

      if (!urlOrgId) {
        setTimeout(() => {
          if (!org) {
            const resolvedOrg = resolveFallbackOrg(userData);
            if (resolvedOrg) {
              setOrg(resolvedOrg);
            } else {
              setOrg(userData.orgs[0].orgID);
              setSelectOrgOpen(true);
            }
          }
        }, 100);
      }
    }
  }, [user, userData, org, window.location.search]);

  // Enhanced setOrg that includes tracking
  const setOrgWithTracking = (newOrgId) => {
    if (newOrgId && newOrgId !== org) {
      markManualModalClose(); // Prevent modal from reopening
      setOrg(newOrgId);
      trackOrgEntry(newOrgId);
    } else {
      setOrg(newOrgId);
    }
  };

  // Handle org changes
  useEffect(() => {
    if (!org) return;

    // Clean up previous listeners only on org change
    cleanupOrgListeners();

    // Reset data
    resetOrgScopedState();

    // Reset loading states for org data - arrays will be watched by useEffects above
    updateLoadingState({
      orgSelected: true,
      orgDataLoaded: false,
      accountsLoaded: false,
      fundsLoaded: false,
      contactsLoaded: false,
    });
  }, [org]);

  // Recovery mechanism: Re-setup listeners if data doesn't arrive within reasonable time
  useEffect(() => {
    if (!org || !authHook?.db) return;

    const timeoutId = setTimeout(() => {
      // If we still don't have data after 5 seconds, try to re-setup listeners
      if (
        !loadingState.accountsLoaded ||
        !loadingState.fundsLoaded ||
        !loadingState.contactsLoaded ||
        !loadingState.orgDataLoaded
      ) {
        cleanupOrgListeners();
        setupOrgListeners();
      }
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [
    org,
    authHook?.db,
    loadingState.accountsLoaded,
    loadingState.fundsLoaded,
    loadingState.contactsLoaded,
    loadingState.orgDataLoaded,
  ]);

  // Broken org detection: Separate from normal loading check
  // This detects when Firestore RETURNS empty data (corruption), not just waiting for data
  // Normal loading (`loaded` state) checks if data exists - this checks if data is MISSING after load attempt
  useEffect(() => {
    if (!org || !orgData) return;

    // Skip un-onboarded orgs (they legitimately have no data yet)
    if (orgData.onboarded === false) return;

    // CRITICAL: Only check when Firestore listeners have fired and returned data (or empty)
    // loadingState.accountsLoaded means "we got a response from Firestore" (even if empty)
    // This is different from loaded=true which requires data.length > 0
    if (
      !loadingState.accountsLoaded ||
      !loadingState.fundsLoaded ||
      !loadingState.contactsLoaded
    ) {
      return;
    }

    // If normal loading succeeded, don't show broken modal (app is working)
    if (loaded) {
      // Clear broken state if it was set
      if (brokenOrgDetected) {
        setBrokenOrgDetected(false);
        setBrokenOrgId(null);
      }
      return;
    }

    // Don't re-show modal if user manually closed it recently (for THIS specific org)
    if (wasRecentlyClosedManually()) {
      return;
    }

    // CRITICAL: Check if org document exists at all
    if (!orgData) {
      console.error("Broken org detected: Org document does not exist", org);
      if (!brokenOrgDetected) {
        Sentry.withScope((scope) => {
          scope.setTag("orgId", org);
          scope.setTag("operation", "broken_org_detected");
          scope.setExtra("reason", "org_doc_missing");
          Sentry.captureMessage("Broken org detected: org doc missing", "error");
        });
      }
      setBrokenOrgDetected(true);
      setBrokenOrgId(org);
      return; // Early return - no need to check child data
    }

    // Skip check for un-onboarded orgs (they're still being set up)
    if (orgData.onboarded === false) {
      setBrokenOrgDetected(false);
      setBrokenOrgId(null);
      return;
    }

    // Check if org is missing required child data
    const isMissingAccounts = accounts.length === 0;
    const isMissingFunds = funds.length === 0;
    const isMissingContacts = contacts.length === 0;
    const isMissingAccountsHierarchy =
      !accountsHierarchy || Object.keys(accountsHierarchy).length === 0;
    const isMissingFundsHierarchy =
      !fundsHierarchy || Object.keys(fundsHierarchy).length === 0;

    if (
      isMissingAccounts ||
      isMissingFunds ||
      isMissingContacts ||
      isMissingAccountsHierarchy ||
      isMissingFundsHierarchy
    ) {
      console.error("Broken org detected: Missing required child data", org);
      if (!brokenOrgDetected) {
        Sentry.withScope((scope) => {
          scope.setTag("orgId", org);
          scope.setTag("operation", "broken_org_detected");
          scope.setExtra("missing", {
            accounts: isMissingAccounts,
            funds: isMissingFunds,
            contacts: isMissingContacts,
            accountsHierarchy: isMissingAccountsHierarchy,
            fundsHierarchy: isMissingFundsHierarchy,
          });
          Sentry.captureMessage(
            "Broken org detected: missing required child data",
            "error",
          );
        });
      }
      // Set broken org state - BrokenOrgModal will open based on brokenOrgDetected
      setBrokenOrgDetected(true);
      setBrokenOrgId(org);
    } else {
      // Org is healthy, clear broken state
      setBrokenOrgDetected(false);
      setBrokenOrgId(null);
    }
  }, [
    org,
    orgData,
    loaded,  // Cancel broken check if normal loading succeeds
    loadingState.accountsLoaded,
    loadingState.fundsLoaded,
    loadingState.contactsLoaded,
    brokenOrgDetected,  // Need this to clear state when loaded becomes true
    // Note: Data arrays NOT in deps to prevent re-checks during loading
    // Note: Uses `loaded` to cancel detection if normal loading succeeds
  ]);

  // Load the org's accounts, funds, contacts, and org data
  useEffect(() => {
    if (authHook && !authHook.isLoadingFirebase && user && org) {
      setupOrgListeners();

      // DON'T cleanup listeners here - they should stay active!
      // Cleanup only happens when org changes (in the org change useEffect)
    }
  }, [authHook.db, user, org, authHook]);

  // Take-control re-setup (GH#181). The orgData snapshot can resolve before the
  // control-sessions listener loads; for a T3Admin with no membership it then
  // hits the no-membership teardown at the top of the orgData snapshot, which
  // runs cleanupOrgListeners() and leaves orgData null. When the control
  // session subsequently arrives, re-establish the org listeners immediately
  // instead of waiting for the 5s recovery timer. setupOrgListeners is
  // idempotent (no-ops if orgData is already subscribed), so this is safe even
  // when it runs alongside the org-load effect above.
  useEffect(() => {
    if (!authHook?.db || !user || !org) return;
    const hasControlSessionForOrg = controlSessions.some(
      (s) => s.orgId === org,
    );
    if (hasControlSessionForOrg && !unsubscribeFunctionsRef.current.orgData) {
      setupOrgListeners();
    }
  }, [controlSessions, org, authHook?.db, user]);

  // Legacy compatibility properties
  const isParentDataLoaded = loadingState.userLoaded;
  const isChildDataLoaded =
    loadingState.orgDataLoaded &&
    loadingState.accountsLoaded &&
    loadingState.fundsLoaded &&
    loadingState.contactsLoaded &&
    // Also check actual data exists
    accounts.length > 0 &&
    funds.length > 0 &&
    contacts.length > 0 &&
    !!orgData;
  const userStillLoading = !loadingState.userChecked;

  // Reset invite check state when user changes
  useEffect(() => {
    setInviteCheckState({
      checking: false,
      checked: false,
      found: false,
      waiting: false,
    });
  }, [user?.uid]);

  // Single-fund mode: orgs with exactly one usable fund get fund inputs
  // hidden/auto-filled and reports default to Total columns. Purely derived
  // from the live funds listener, so adding a second fund flips this off
  // everywhere without a reload. isUsableFund is the same predicate the fund
  // pickers filter by, so "one fund in dropdowns" and "single-fund mode" can
  // never disagree. Zero funds (mid-onboarding) is NOT single-fund mode.
  const activeFunds = useMemo(() => funds.filter(isUsableFund), [funds]);
  const isSingleFundOrg = activeFunds.length === 1;
  const defaultFund = isSingleFundOrg ? activeFunds[0] : null;

  // One switch, SIX slices — narrowed from nine on 2026-09-05, and the reason is
  // worth stating because the missing three look like an oversight.
  //
  // These six are proven: supabase/tests/refdata-parity.sh compares them against
  // live Firestore for every org in the estate and exits 0, and
  // hierarchy-parity.mjs reproduces the three hierarchy documents byte-exact.
  //
  // features, subscriptionData and orgData are DELIBERATELY still Firestore,
  // because the billing domain has not been migrated at all: there is no
  // billing mapping in migration/src/map/, and public.org_billing holds ZERO
  // rows on both the local estate and hosted dev. v_org_effective_features
  // therefore falls back to its all-false literal, so serving `features` from
  // Postgres today would switch banking, labels, payroll, saved report packages
  // and ML categorisation OFF for every org — silently, since an all-false
  // feature map is a valid-looking answer. subscriptionData has the same
  // problem, and orgData carries features, so all three move together when the
  // billing wave lands. useOrgRefdata already MAPS them; nothing consumes the
  // mapping yet.
  //
  // Falling back per-slice within the six would let a half-loaded Postgres path
  // mix with listener data and produce a chart that is internally inconsistent —
  // worse to debug than either source being wrong on its own. Hence one switch.
  const refSource = pgRefdata.loaded
    ? { ...pgRefdata, features, orgData, subscriptionData }
    : { accounts, funds, labels, accountsHierarchy, fundsHierarchy, labelsHierarchy,
        features, orgData, subscriptionData };


  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        userData,
        setUserData,
        org,
        setOrg: setOrgWithTracking,
        orgData,
        subscriptionData: refSource.subscriptionData,
        orgRoles,
        accounts: refSource.accounts,
        funds: refSource.funds,
        setFunds,
        isSingleFundOrg,
        defaultFund,
        labels: refSource.labels,
        setLabels,
        contacts,
        accountsHierarchy: refSource.accountsHierarchy,
        fundsHierarchy: refSource.fundsHierarchy,
        setFundsHierarchy,
        labelsHierarchy: refSource.labelsHierarchy,
        setLabelsHierarchy,
        dateRangeForTx,
        setDateRangeForTx,
        loaded,
        setIsChildDataLoaded: () => {}, // No-op for legacy compatibility
        userStillLoading,
        setOpenSubscribe,
        openSubscribe,
        selectOrgOpen,
        setSelectOrgOpen,
        markManualModalClose,
        isParentDataLoaded,
        setAccounts,
        setAccountsHierarchy,
        setContacts,
        setUserStillLoading: () => {}, // No-op for legacy compatibility
        deleteQueue,
        setDeleteQueue,
        contactMapping,
        contactAccountFundMatches,
        mlModelState,
        predictionsReady: canRunPredictions(
          orgData,
          mlModelState,
          contactAccountFundMatches,
          hasOpenExpectedTransactions,
        ),
        features: refSource.features,
        donors,
        donationSources,
        donationSourcesLoaded: loadingState.donationSourcesLoaded,
        freeTrialNotified,
        setFreeTrialNotified,
        openTrialNotification,
        setOpenTrialNotification,
        authHook,
        inviteCheckState,
        controlSessions,
        brokenOrgDetected,
        setBrokenOrgDetected,
        brokenOrgId,
      }}>
      <ThemeUserSync />
      <TransactionHookRegistryProvider>
        {children}
      </TransactionHookRegistryProvider>
    </UserContext.Provider>
  );
};

export default UserProvider;
